import {useEffect, useMemo, useState} from 'react';
import {AutoCompleteItemValue} from '../../../components/shared/nextgen/selector/types';
import {VisibleDays} from '../../../features/report/types';
import {setTimeReportData} from '../../../features/time-report';
import {getTimeReportV2, getTimeReportV2Daily} from '../../../features/time-report-v2';
import {useAppDispatch, useAppSelector} from '../../../hooks/hooks';
import {canSeeOtherTimeReportItems} from '../../../features/profile/entityPermissions';
import {mapTimeReportV2DailyItem, mapTimeReportV2Item} from '../helpers/map-time-report-items';
import {TimeReportV2DailyEmployeeTotal} from '../../../features/time-report-v2/types';

const getSelectedIds = (values?: AutoCompleteItemValue[]): number[] => (
    values?.map((value) => value.originalId as number).filter((value): value is number => typeof value === 'number') ?? []
);

const useTimeReportView = (detailedByDay: boolean, visibleDays: VisibleDays) => {
    const dispatch = useAppDispatch();
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<unknown>(null);
    const [dailyEmployeeTotals, setDailyEmployeeTotals] = useState<TimeReportV2DailyEmployeeTotal[]>([]);
    const profile = useAppSelector((selector) => selector.profile);
    const filter = useAppSelector((selector) => selector.timeTrackingFilters.filter);
    const data = useAppSelector((store) => store.timereport.data);
    const canViewOtherTimeReportItems = canSeeOtherTimeReportItems(profile);
    const ownEmployeeId = profile.id;

    const selectedEmployeeIds = useMemo(() => {
        if (canViewOtherTimeReportItems) {
            return getSelectedIds(filter.propertyFilter.selectedEmployees);
        }

        return ownEmployeeId ? [ownEmployeeId] : [];
    }, [canViewOtherTimeReportItems, filter.propertyFilter.selectedEmployees, ownEmployeeId]);

    const selectedProjectIds = useMemo(
        () => getSelectedIds(filter.propertyFilter.selectedProjects),
        [filter.propertyFilter.selectedProjects]
    );

    const selectedProjectTypeIds = useMemo(
        () => getSelectedIds(filter.propertyFilter.selectedProjectTypes),
        [filter.propertyFilter.selectedProjectTypes]
    );

    useEffect(() => {
        const startDate = filter.dateRangeFilter.startDate;
        const endDate = filter.dateRangeFilter.endDate;

        if (!startDate || !endDate) {
            setTimeReportData([]);
            setDailyEmployeeTotals([]);
            setLoading(false);
            return;
        }

        let active = true;
        setLoading(true);
        setError(null);

        const load = async () => {
            const showAbsences = filter.propertyFilter.showAbsences ?? true;

            if (detailedByDay) {
                const report = await dispatch(getTimeReportV2Daily({
                    from: startDate,
                    to: endDate,
                    employeeIds: selectedEmployeeIds,
                    projectIds: selectedProjectIds,
                    projectTypeIds: selectedProjectTypeIds,
                    visibleDays,
                    showAbsences,
                })).unwrap();
                const items = report.items ?? [];

                if (active) {
                    setDailyEmployeeTotals(report.employeeTotals ?? []);
                    setTimeReportData(items.map(mapTimeReportV2DailyItem));
                }
                return;
            }

            const items = await dispatch(getTimeReportV2({
                from: startDate,
                to: endDate,
                employeeIds: selectedEmployeeIds,
                projectIds: selectedProjectIds,
                projectTypeIds: selectedProjectTypeIds,
                showAbsences,
            })).unwrap();

            if (active) {
                setDailyEmployeeTotals([]);
                setTimeReportData(items.map(mapTimeReportV2Item));
            }
        };

        void load()
            .catch((err) => {
                console.error('TimeReportV2 load failed', err);
                if (active) {
                    setError(err);
                    setDailyEmployeeTotals([]);
                    setTimeReportData([]);
                }
            })
            .finally(() => {
                if (active) {
                    setLoading(false);
                }
            });

        return () => {
            active = false;
        };
    }, [
        detailedByDay,
        dispatch,
        filter.dateRangeFilter.endDate,
        filter.dateRangeFilter.startDate,
        filter.propertyFilter.showAbsences,
        selectedEmployeeIds,
        selectedProjectIds,
        selectedProjectTypeIds,
        visibleDays,
    ]);

    return {data, loading, error, dailyEmployeeTotals};
};

export default useTimeReportView;
